import { useQuery } from "@apollo/client";
import { compact } from "lodash";
import {
  Route,
  Switch,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { Redirect } from "react-router-dom";
import {
  GetOrganizationSettingsQuery,
  GetOrganizationSettingsQueryVariables,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import Integrations from "@apps/org-settings/components/integrations";
import Notifications from "@apps/org-settings/components/notifications";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Layout from "@components/layout/layout";
import Tabs from "@components/tabs/tabs";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import { role } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import Billing from "./components/billing";
import CareerTrackForm from "./components/competencies/career-track-form/career-track-form";
import CompetencyFormPage from "./components/competencies/competency-form-page";
import RolesAndCompetencies from "./components/competencies/roles-and-competencies";
import CoreValues from "./components/core-values";
import DirectorySync from "./components/directory-sync";
import Members from "./components/members";
import Settings from "./components/settings";
import TalentIndicators from "./components/talent-indicators";
import Teams from "./components/teams";
import getOrganizationSettingsQuery from "./graphql/get-organization-settings-query";

type OrgSettingsTab = {
  label: string;
  value: string;
  to: string;
};

const IT_ADMIN_ALLOWED_TABS = new Set([
  "directory-sync",
  "integrations",
  "notifications",
]);

const buildOrgSettingsTabs = (
  organizationId: number,
  label: ReturnType<typeof useLabel>
): OrgSettingsTab[] =>
  compact<OrgSettingsTab>([
    {
      label: "Settings",
      value: "settings",
      to: `/settings/organization/${organizationId}/settings`,
    },
    {
      label: "People",
      value: "members",
      to: `/settings/organization/${organizationId}/members`,
    },
    {
      label: label("team", { capitalize: true, pluralize: true }),
      value: "teams",
      to: `/settings/organization/${organizationId}/teams`,
    },
    {
      label: "Core Values",
      value: "core-values",
      to: `/settings/organization/${organizationId}/core-values`,
    },
    {
      label: `Roles and ${label("competency", {
        capitalize: true,
        pluralize: true,
      })}`,
      value: "competencies",
      to: `/settings/organization/${organizationId}/competencies`,
    },
    waffle.flag_is_active("talent-indicators") && {
      label: "Talent Indicators",
      value: "talent-indicators",
      to: `/settings/organization/${organizationId}/talent-indicators`,
    },
    {
      label: "Billing",
      value: "billing",
      to: `/settings/organization/${organizationId}/billing`,
    },
    {
      label: "HRIS Sync",
      value: "directory-sync",
      to: `/settings/organization/${organizationId}/directory-sync`,
    },
    {
      label: "Integrations",
      value: "integrations",
      to: `/settings/organization/${organizationId}/integrations`,
    },
    {
      label: "Notifications",
      value: "notifications",
      to: `/settings/organization/${organizationId}/notifications`,
    },
  ]);

const Nav: React.FC<{ tabs: OrgSettingsTab[] }> = ({ tabs }) => {
  const { navId } = useParams<{ navId?: string }>();
  const displayedTab = tabs.find((tab) => tab.value === navId);
  const selectedValue = displayedTab?.value ?? tabs[0]?.value;

  return (
    <Tabs<string>
      options={tabs}
      selectedValue={selectedValue}
      onClickOptionValue={() => {}}
    />
  );
};

const OrgSettings = () => {
  const label = useLabel();
  const currentOrganization = currentOrganizationVar();
  useDocumentTitle(`${label("organization", { capitalize: true })} settings`);
  const location = useLocation<TFLocationState>();
  const { path } = useRouteMatch();
  const { organizationId: organizationIdParam } = useParams<{
    organizationId: string;
  }>();
  const organizationId =
    parseInt(organizationIdParam) || currentOrganization.id;

  // HOOKS
  const { data, loading } = useQuery<
    GetOrganizationSettingsQuery,
    GetOrganizationSettingsQueryVariables
  >(getOrganizationSettingsQuery);
  const organizations = data?.me?.organizations
    ? assertEdgesNonNull(data.me.organizations)
    : [];
  const selectedOrganization = organizations.find(
    ({ id }) => id === organizationId
  );
  const membershipRole = selectedOrganization?.userMembership?.role ?? null;
  const hasSettingsAccess =
    membershipRole !== null &&
    [role.globalAdmin, role.peopleAdmin, role.itAdmin].includes(membershipRole);

  if (selectedOrganization?.userMembership && !hasSettingsAccess) {
    return <Redirect to="/" />;
  }

  const tabs = selectedOrganization
    ? buildOrgSettingsTabs(selectedOrganization.id, label)
    : [];
  const filteredTabs =
    membershipRole === role.itAdmin
      ? tabs.filter((tab) => IT_ADMIN_ALLOWED_TABS.has(tab.value))
      : tabs;
  const allowedTabs = filteredTabs;
  const firstAllowedTab = allowedTabs[0];
  const buildTabPath = (tabValue: string) => {
    const targetOrganizationId = selectedOrganization?.id ?? organizationId;
    return targetOrganizationId
      ? `/settings/organization/${targetOrganizationId}/${tabValue}`
      : "/";
  };
  const defaultTabPath = firstAllowedTab
    ? buildTabPath(firstAllowedTab.value)
    : "/";
  const hasAccess = (tabValue: string) => {
    if (!membershipRole) {
      return false;
    }
    if (membershipRole === role.itAdmin) {
      return IT_ADMIN_ALLOWED_TABS.has(tabValue);
    }
    return [role.globalAdmin, role.peopleAdmin].includes(membershipRole);
  };

  return (
    <Layout>
      <Layout.Header
        breadcrumbs={compact([
          {
            title: "Organization Settings",
            url: `/settings/organization/`,
          },
          selectedOrganization && {
            title: selectedOrganization?.name,
            url: `/settings/organization/${organizationId}/settings`,
          },
        ])}
      />
      <Layout.Container loading={loading}>
        <Layout.Main width="full" className="bg-gray-50">
          {selectedOrganization && (
            <div className="">
              {allowedTabs.length > 0 ? (
                <>
                  <div className="mb-6">
                    <Switch>
                      <Route path={[`${path}/:navId`, path]}>
                        <Nav tabs={allowedTabs} />
                      </Route>
                    </Switch>
                  </div>

                  <div
                    className={classNames(
                      location.pathname.includes("/members")
                        ? ""
                        : "p-6 bg-white shadow sm:rounded-md text-gray-800"
                    )}
                  >
                    <Switch>
                      <Route exact path={[`${path}/settings`, `${path}`]}>
                        {hasAccess("settings") ? (
                          <Settings organization={selectedOrganization} />
                        ) : (
                          <Redirect to={defaultTabPath} />
                        )}
                      </Route>
                      <Route exact path={`${path}/members`}>
                        {hasAccess("members") ? (
                          <Members organization={selectedOrganization} />
                        ) : (
                          <Redirect to={defaultTabPath} />
                        )}
                      </Route>
                      <Route exact path={`${path}/teams`}>
                        {hasAccess("teams") ? (
                          <Teams organization={selectedOrganization} />
                        ) : (
                          <Redirect to={defaultTabPath} />
                        )}
                      </Route>
                      <Route exact path={`${path}/core-values`}>
                        {hasAccess("core-values") ? (
                          <CoreValues
                            organizationId={selectedOrganization.id}
                          />
                        ) : (
                          <Redirect to={defaultTabPath} />
                        )}
                      </Route>
                      <Route exact path={`${path}/billing`}>
                        {hasAccess("billing") ? (
                          <Billing organization={selectedOrganization} />
                        ) : (
                          <Redirect to={defaultTabPath} />
                        )}
                      </Route>
                      <Route exact path={`${path}/directory-sync`}>
                        {hasAccess("directory-sync") ? (
                          <DirectorySync organization={selectedOrganization} />
                        ) : (
                          <Redirect to={defaultTabPath} />
                        )}
                      </Route>
                      <Route exact path={`${path}/integrations`}>
                        {hasAccess("integrations") ? (
                          <Integrations organization={selectedOrganization} />
                        ) : (
                          <Redirect to={defaultTabPath} />
                        )}
                      </Route>
                      <Route exact path={`${path}/notifications`}>
                        {hasAccess("notifications") ? (
                          <Notifications organization={selectedOrganization} />
                        ) : (
                          <Redirect to={defaultTabPath} />
                        )}
                      </Route>
                      {waffle.flag_is_active("talent-indicators") && (
                        <Route exact path={`${path}/talent-indicators`}>
                          {hasAccess("talent-indicators") ? (
                            <TalentIndicators
                              organization={selectedOrganization}
                            />
                          ) : (
                            <Redirect to={defaultTabPath} />
                          )}
                        </Route>
                      )}
                      <Route exact path={`${path}/competencies`}>
                        {hasAccess("competencies") ? (
                          <RolesAndCompetencies
                            organizationId={selectedOrganization.id}
                          />
                        ) : (
                          <Redirect to={defaultTabPath} />
                        )}
                      </Route>
                      <Route exact path={`${path}/competencies/new`}>
                        {hasAccess("competencies") ? (
                          <CompetencyFormPage
                            organizationId={selectedOrganization.id}
                          />
                        ) : (
                          <Redirect to={defaultTabPath} />
                        )}
                      </Route>
                      <Route
                        exact
                        path={`${path}/competencies/edit/:competencyId`}
                      >
                        {hasAccess("competencies") ? (
                          <CompetencyFormPage
                            organizationId={selectedOrganization.id}
                          />
                        ) : (
                          <Redirect to={defaultTabPath} />
                        )}
                      </Route>
                      <Route exact path={`${path}/career-track/new`}>
                        {hasAccess("competencies") ? (
                          <CareerTrackForm
                            organizationId={selectedOrganization.id}
                          />
                        ) : (
                          <Redirect to={defaultTabPath} />
                        )}
                      </Route>
                      <Route
                        exact
                        path={`${path}/career-track/edit/:careerTrackId`}
                      >
                        {hasAccess("competencies") ? (
                          <CareerTrackForm
                            organizationId={selectedOrganization.id}
                          />
                        ) : (
                          <Redirect to={defaultTabPath} />
                        )}
                      </Route>
                    </Switch>
                  </div>
                </>
              ) : (
                <div className="p-6 bg-white shadow sm:rounded-md text-gray-800">
                  <p>
                    You do not have access to this organization&apos;s settings.
                  </p>
                </div>
              )}
            </div>
          )}
        </Layout.Main>
      </Layout.Container>
    </Layout>
  );
};

export default OrgSettings;
